import { useCallback, useEffect, useRef, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import AtlasToolbarIconButton from "./AtlasToolbarIconButton";

const MIN_REFRESH_SPIN_MS = 700;

export function useRefreshSpin(externalSpinning = false) {
  const [clickSpinning, setClickSpinning] = useState(false);
  const clickSpinTimerRef = useRef(null);

  const clearClickSpinTimer = useCallback(() => {
    if (clickSpinTimerRef.current) {
      clearTimeout(clickSpinTimerRef.current);
      clickSpinTimerRef.current = null;
    }
  }, []);

  const triggerRefreshSpin = useCallback(() => {
    setClickSpinning(true);
    clearClickSpinTimer();
    clickSpinTimerRef.current = setTimeout(() => {
      setClickSpinning(false);
      clickSpinTimerRef.current = null;
    }, MIN_REFRESH_SPIN_MS);
  }, [clearClickSpinTimer]);

  useEffect(() => clearClickSpinTimer, [clearClickSpinTimer]);

  return {
    isSpinning: externalSpinning || clickSpinning,
    triggerRefreshSpin,
  };
}

export default function AtlasRefreshIconButton({
  onClick,
  spinning = false,
  disabled = false,
  title = "Refresh",
  iconClassName = "h-4 w-4",
  className = "",
  buttonClassName = "",
  iconSize,
  useToolbarButton = true,
}) {
  const { isSpinning, triggerRefreshSpin } = useRefreshSpin(spinning);

  const handleClick = useCallback(() => {
    if (disabled) return;
    triggerRefreshSpin();
    onClick?.();
  }, [disabled, onClick, triggerRefreshSpin]);

  const icon = (
    <FaSyncAlt
      size={iconSize}
      className={[iconClassName, isSpinning ? "animate-spin" : ""]
        .filter(Boolean)
        .join(" ")}
    />
  );

  if (useToolbarButton) {
    return (
      <AtlasToolbarIconButton
        onClick={handleClick}
        title={title}
        disabled={disabled}
        className={className}
      >
        {icon}
      </AtlasToolbarIconButton>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      className={buttonClassName || className}
    >
      {icon}
    </button>
  );
}
